import {
    PaymentGroup,
    PaymentGroupMeta,
    PaymentMethod,
    PaymentMethodMeta,
    PaymentGroupOptions,
    PaymentGroupActions,
} from "./Payment";
import { ApiFetchState as FetchState } from "@swiggy-private/payment-core";
import { ApiResponseJuspayMeta, JuspayTokenizationMeta, CardBrandDetails } from "@swiggy-private/payment-types";

export interface ICardPaymentGroup extends PaymentGroup<CardPaymentGroupMeta, CardPaymentMethodData> {
    paymentActions: CardPaymentGroupActions;
    options: CardPaymentGroupOptions;
    isSdkAvailable: boolean;
    cardGroupCotainerClassName?: string;
    groupTitle?: string;
    payCtaText?: string;
    payCtaClassName?: string;
    juspayMeta?: ApiResponseJuspayMeta;
}

export type CardPaymentGroupActions = PaymentGroupActions<CardPaymentMethodData> & CardSpecificActions;

export enum SECURE_CARD_INFO_ACTION {
    GOT_IT = "click-got-it",
    CLOSE = "click-dismiss-tokenization-guidelines",
    SECURE_CARD_AND_PAY = "Secure-card-and-pay",
    SKIP_FOR_NOW = "Skip-for-now",
}

export interface CardSpecificActions {
    getCardAmount?: (methodData: CardPaymentMethodData) => [FetchState, FoodCardData] | null;
    onSaveCardClick?: (value: boolean) => void;
    onSecureCardInfoView?: VoidFunction;
    onHideCvv?: VoidFunction;
    onSecureCardInfoClickAction?: (action: SECURE_CARD_INFO_ACTION) => void;
    onKnowMoreTokenizationClickAction?: VoidFunction;
}

export interface CardPaymentGroupMeta extends PaymentGroupMeta {
    merchantIdCard: string;
    merchantNameCard: string;
    merchantName: string;
    webMerchantId: string;
    merchantId: string;
    returnToUrl: string;
}

export enum CTA_TYPE {
    REGULAR = "regular",
    EMI = "emi",
}

export interface CTA {
    type: CTA_TYPE;
    text: string;
    enabled: boolean;
    disableText?: string;
}

export interface CardPaymentMethodMeta extends PaymentMethodMeta {
    iconUrl: string;
    promoMsg: string;
    disableMessage: string;
    // below fields do not exist in new card. so they are optional
    cardBalance?: string; // for food card
    cardType?: string;
    cardExpYear?: string;
    nameOnCard?: string;
    isFoodCard?: boolean;
    cvvMandatory?: boolean;
    cvvMessage?: string;
    expired?: boolean;
    cardIsin?: string;
    cardBrand?: string;
    cardToken?: string;
    cardReference?: string;
    cardIssuer?: string;
    // new fields for card tokenization
    tokenizeSupport?: boolean;
    isSecured?: boolean;
    nickname?: string;
    cardNumber?: string;
    // new field for card tokenisation
    token?: unknown;
    providerCategory?: string;
    provider?: string;
    emiTag?: {
        enabled: boolean;
        text: string;
    };
    cta?: CTA[];
}

export interface CardPaymentMethodData extends PaymentMethod<CardPaymentMethodMeta> {
    bin?: string;
    cardFingerprint?: string;
}

export interface CardPaymentGroupOptions extends PaymentGroupOptions {
    weAcceptAllLogos: CardLogos;
    weAcceptFoodCardLogos: CardLogos;
    weAcceptNonFoodCardLogos: CardLogos;
}

export interface CardLogos {
    [key: string]: CardLogoObject;
}

export interface CardLogoObject {
    enabled: number;
    id: string;
    small?: boolean;
}

export interface FoodCardData {
    balance: string | number;
    isBalanceSufficent: boolean;
    cvvRequired: boolean;
    additionalBalanceRequired: number;
}

export interface NCEMIEvents {
    onPayInEMIClickAction: () => void;
}

export enum ADD_NEW_CARD_PROCEED_ACTION_STATUS {
    CLICKED = "clicked",
    PAYMENT_IN_PROGRESS = "payment-in-progress",
    INVALID_CARD_DETAILS = "invalid-card-details",
    PROCEED_TO_PAY = "proceed-to-pay",
    FAILED_TO_PROCEED = "failed-to-proceed",
    CARD_NOT_ELIGIBLE_FOR_EMI = "card-not-eligible-for-emi",
}

export interface AddNewCardProceedMeta {
    cardBrand?: string | null;
    skipClientCardValidation?: boolean;
}

export interface AddNewCardCommonProps {
    onSecureCardInfoView?: VoidFunction;
    onSecureCardInfoClickAction?: (action: SECURE_CARD_INFO_ACTION) => void;
    onKnowMoreTokenizationClickAction?: VoidFunction;
    options?: CardPaymentGroupOptions;
    juspayTokenizationMeta?: JuspayTokenizationMeta;
    onCardNumberChange: (cardBin: string) => void;
    onCardNumberBlur?: (cardNumber: string) => void;
    cardBrandDetails: CardBrandDetails;
    onSaveCardClick?: (isChecked: boolean) => void;
    onProceedToPayClickAction?: (secureCard: boolean, discountText: string) => void;
    skipClientCardValidation: boolean;
    onProceedToPayClick: (actionStatus: ADD_NEW_CARD_PROCEED_ACTION_STATUS, meta?: AddNewCardProceedMeta) => void;
    /** Shown above the primary CTA (e.g. instamart save flow). */
    saveCardCtaHelperText?: string;
}

export interface CardSpecificData {
    isSdkAvailable: boolean;
}

export interface ICardOffer {
    cardBin?: string;
    offerAmount?: string;
    payableAmount?: string;
    discountTitle?: string;
    couponApplicable?: boolean;
    rewardApplicable?: boolean;
}

export interface ICardFingerprint {
    /* eslint-disable  @typescript-eslint/naming-convention */
    card_fingerprint: string;
    par: string;
}

export interface IRewardOfferNewCard {
    split_payment_methods: string[];
    invalid_method: string[];
    payable_amount?: string;
    coupon_applicable?: boolean;
    reward_applicable?: boolean;
    /* eslint-enable  @typescript-eslint/naming-convention */
}
